// packages/web/src/components/Layout/FilterPanel.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Slider,
  Chip,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  Fab,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import { Filters, UTR_MIN, UTR_MAX, NTRP_MIN, NTRP_MAX } from '@matchpoint/shared';
import { CourtSearchSelect } from '../Common/CourtSearchSelect';
import { LocationSearchSelect } from '../Common/LocationSearchSelect';

// Extended Filters interface to include radius and rating ranges
interface ExtendedFilters extends Filters {
  radius?: number;
}

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: 0,
  width: 'auto',
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(2), // Position on the right side
  zIndex: 1000,
  boxShadow: 'none',
  backgroundColor: 'transparent',
}));

const DrawerContent = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '400px', // Add max width for drawer
  padding: theme.spacing(1.5),
  backgroundColor: '#ffffff',
  maxHeight: '85vh',
  overflowY: 'auto',
  margin: '0 auto', // Center drawer content
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5),
  paddingBottom: theme.spacing(1),
  borderBottom: '1px solid #e0e0e0',
}));

const FilterTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(0.75),
  width: '100%',
  padding: theme.spacing(1, 1.5),
  fontSize: '0.875rem',
  fontWeight: 500,
  textTransform: 'none',
}));

const ApplyButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
}));

const ClearButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.grey[300],
  },
}));

const SliderSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 0.5),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '0.875rem',
  marginBottom: theme.spacing(0.75),
}));

const CompactAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': {
    padding: theme.spacing(0.5, 0),
    minHeight: 36,
    '&.Mui-expanded': {
      minHeight: 36,
    },
  },
  '& .MuiAccordionSummary-content': {
    margin: '8px 0',
    '&.Mui-expanded': {
      margin: '8px 0',
    },
  },
  '& .MuiAccordionDetails-root': {
    padding: theme.spacing(0, 0, 1.5, 0),
  },
}));

const FilterFab = styled(Fab)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  width: 48,
  height: 48,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const CompactChip = styled(Chip)(({ theme }) => ({
  height: 22,
  fontSize: '0.75rem',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const CompactFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiInputBase-root': {
    fontSize: '0.875rem',
  },
  '& .MuiOutlinedInput-input': {
    padding: '10px 14px',
  },
  '& .MuiInputLabel-outlined': {
    transform: 'translate(14px, 12px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
}));

const CompactSlider = styled(Slider)(({ theme }) => ({
  '& .MuiSlider-markLabel': { 
    fontSize: '0.65rem',
    top: 26,
  },
  '& .MuiSlider-valueLabel': {
    fontSize: '0.75rem',
    padding: '2px 6px',
  },
  '& .MuiSlider-thumb': { 
    width: 16,
    height: 16,
  },
  '& .MuiSlider-rail': {
    height: 3,
  },
  '& .MuiSlider-track': {
    height: 3,
  },
}));

interface FilterPanelProps {
  filters: ExtendedFilters;
  onApplyFilters: (filters: ExtendedFilters) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideFab?: boolean;
}

// Format label functions outside component
const formatUtrLabel = (value: number | null) => {
  if (value === null || value === UTR_MIN) return 'Unrated';
  return value.toFixed(1);
};

const formatNtrpLabel = (value: number | null) => {
  if (value === null || value === NTRP_MIN) return 'Unrated';
  return value.toFixed(1);
};

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onApplyFilters,
  open: externalOpen,
  onOpenChange,
  hideFab = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const drawerOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  
  const [filterForm, setFilterForm] = useState<ExtendedFilters>(filters);
  const [courtValue, setCourtValue] = useState(filters.court === 'any' ? '' : filters.court || '');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    location: true,
    court: false,
    player: false,
    time: false,
  });
  
  // Track if there are any active filters (not default values)
  const activeFiltersCount = Object.entries(filterForm).filter(([key, value]) => {
    if (key === 'timezone') return false;
    if (key === 'level' && value === 'any') return false;
    if (key === 'court' && (value === 'any' || value === '')) return false;
    if (key === 'duration' && value === 'any') return false;
    if (key === 'country' && value === 'any') return false;
    if (key === 'city' && value === '') return false;
    if (key === 'radius' && value === 10) return false;
    if ((key === 'utrMin' || key === 'ntrpMin') && (value === null || value === UTR_MIN || value === NTRP_MIN)) return false;
    if ((key === 'utrMax' || key === 'ntrpMax') && (value === null || value === UTR_MAX || value === NTRP_MAX)) return false;
    return value !== null && value !== undefined && value !== 'any' && value !== '';
  }).length;

  useEffect(() => {
    setFilterForm(filters);
    setCourtValue(filters.court === 'any' ? '' : filters.court || '');
  }, [filters]);

  const handleOpenDrawer = () => {
    if (onOpenChange) {
      onOpenChange(true);
    } else {
      setInternalOpen(true);
    }
  };
  
  const handleCloseDrawer = () => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      setInternalOpen(false);
    }
  };

  const handleApplyFilters = () => {
    const updatedFilters = {
      ...filterForm,
      court: courtValue || 'any',
    };
    onApplyFilters(updatedFilters);
    handleCloseDrawer();
  };

  const handleClearFilters = () => {
    const clearedFilters: ExtendedFilters = {
      level: 'any',
      utrMin: null,
      utrMax: null,
      ntrpMin: null,
      ntrpMax: null,
      court: 'any',
      duration: 'any',
      city: '',
      country: 'any',
      timezone: filterForm.timezone,
      radius: 10,
    };
    setFilterForm(clearedFilters);
    setCourtValue('');
    onApplyFilters(clearedFilters);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderFilterSection = (
    key: string,
    title: string,
    icon: React.ReactNode,
    content: React.ReactNode
  ) => (
    <CompactAccordion 
      expanded={expandedSections[key]}
      onChange={() => toggleSection(key)}
      disableGutters
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionTitle>
          {icon}
          {title}
        </SectionTitle>
      </AccordionSummary>
      <AccordionDetails>
        {content}
      </AccordionDetails>
    </CompactAccordion>
  );

  const renderPlayerRatingSliders = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* UTR Rating Slider */}
      <SliderSection>
        <Typography 
          variant="body2" 
          sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 0.5 }}
        >
          UTR: {formatUtrLabel(filterForm.utrMin)} - {formatUtrLabel(filterForm.utrMax)}
        </Typography>
        <CompactSlider
          value={[filterForm.utrMin ?? UTR_MIN, filterForm.utrMax ?? UTR_MAX]}
          onChange={(_, value) => {
            const [min, max] = value as number[];
            setFilterForm(prev => ({ 
              ...prev, 
              utrMin: min === UTR_MIN ? null : min,
              utrMax: max === UTR_MAX ? null : max
            }));
          }}
          valueLabelDisplay="auto"
          valueLabelFormat={formatUtrLabel}
          min={UTR_MIN}
          max={UTR_MAX}
          step={0.5}
          marks={[
            { value: UTR_MIN, label: 'Unrated' },
            { value: 5, label: '5' },
            { value: 10, label: '10' },
            { value: UTR_MAX, label: String(UTR_MAX) },
          ]}
          size="small"
        />
      </SliderSection>

      {/* NTRP Rating Slider */}
      <SliderSection>
        <Typography 
          variant="body2" 
          sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 0.5 }}
        >
          NTRP: {formatNtrpLabel(filterForm.ntrpMin)} - {formatNtrpLabel(filterForm.ntrpMax)}
        </Typography>
        <CompactSlider
          value={[filterForm.ntrpMin ?? NTRP_MIN, filterForm.ntrpMax ?? NTRP_MAX]}
          onChange={(_, value) => {
            const [min, max] = value as number[];
            setFilterForm(prev => ({ 
              ...prev, 
              ntrpMin: min === NTRP_MIN ? null : min,
              ntrpMax: max === NTRP_MAX ? null : max
            }));
          }}
          valueLabelDisplay="auto"
          valueLabelFormat={formatNtrpLabel}
          min={NTRP_MIN}
          max={NTRP_MAX}
          step={0.5}
          marks={[
            { value: NTRP_MIN, label: 'Unrated' },
            { value: 2.5, label: '2.5' },
            { value: 4.0, label: '4.0' },
            { value: NTRP_MAX, label: String(NTRP_MAX) },
          ]}
          size="small"
        />
      </SliderSection>
    </Box>
  );

  return (
    <FilterContainer>
      {!hideFab && (
        <FilterFab onClick={handleOpenDrawer}>
          <TuneIcon />
          {activeFiltersCount > 0 && (
            <CompactChip 
              label={activeFiltersCount}
              color="error"
              size="small"
              sx={{ 
                position: 'absolute',
                top: -6,
                right: -6,
                height: 18,
                minWidth: 18,
                fontSize: '0.65rem',
                '& .MuiChip-label': { px: 0.4 },
              }} 
            />
          )}
        </FilterFab>
      )}
      
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        disableRestoreFocus
        ModalProps={{
          keepMounted: true,
          disableAutoFocus: true,
          disableEnforceFocus: true,
        }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            maxHeight: '85vh',
          }
        }}
      >
        <DrawerContent>
          <DrawerHeader>
            <FilterTitle>
              <TuneIcon sx={{ fontSize: '1.25rem' }} />
              Filters
              {activeFiltersCount > 0 && (
                <CompactChip 
                  label={activeFiltersCount} 
                  size="small" 
                  color="primary"
                  sx={{ ml: 0.5 }}
                />
              )}
            </FilterTitle>
            
            <IconButton 
              onClick={handleCloseDrawer}
              size="small"
              sx={{ padding: '4px' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DrawerHeader>
          
          {renderFilterSection(
            'location',
            'Location',
            <LocationOnIcon />,
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <LocationSearchSelect
                value={filterForm.city || ''}
                onChange={(value, option) => {
                  setFilterForm(prev => ({ 
                    ...prev, 
                    city: value,
                    country: option?.country || prev.country
                  }));
                }}
                type="city"
                label="City"
                placeholder="Search city..."
                size="small"
                fullWidth
              />
              
              <LocationSearchSelect
                value={filterForm.country || ''}
                onChange={(value) => setFilterForm(prev => ({ 
                  ...prev, 
                  country: value || '',
                  city: prev.city && value !== prev.country ? '' : prev.city
                }))}
                type="country"
                label="Country"
                placeholder="Search country..."
                size="small"
                fullWidth
              />
              
              <SliderSection>
                <Typography 
                  variant="body2" 
                  sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 0.5 }}
                >
                  Radius: {filterForm.radius || 10} mi
                </Typography>
                <CompactSlider
                  value={filterForm.radius || 10}
                  onChange={(_, value) => setFilterForm(prev => ({ ...prev, radius: value as number }))}
                  valueLabelDisplay="auto"
                  min={1}
                  max={100}
                  step={1}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 30, label: '30' },
                    { value: 50, label: '50' },
                    { value: 75, label: '75' },
                    { value: 100, label: '100' },
                  ]}
                  size="small"
                />
              </SliderSection>
            </Box>
          )}

          {renderFilterSection(
            'court',
            'Court',
            <SportsTennisIcon />,
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <CourtSearchSelect
                value={courtValue}
                onChange={(value) => setCourtValue(value)}
                label="Tennis Court"
                size="small"
                fullWidth
                includeAnyOption
                anyOptionLabel="Any Court"
              />
            </Box>
          )}

          {renderFilterSection(
            'player',
            'Player Level',
            <PersonIcon />,
            renderPlayerRatingSliders()
          )}

          {renderFilterSection(
            'time',
            'Duration',
            <AccessTimeIcon />,
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <CompactFormControl fullWidth size="small">
                <InputLabel id="duration-label">Duration</InputLabel>
                <Select
                  labelId="duration-label"
                  value={filterForm.duration}
                  label="Duration"
                  onChange={(e) => setFilterForm(prev => ({ ...prev, duration: e.target.value }))}
                >
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value={30}>30 min</MenuItem>
                  <MenuItem value={60}>1 hour</MenuItem>
                  <MenuItem value={90}>1.5 hours</MenuItem>
                  <MenuItem value={120}>2 hours</MenuItem>
                  <MenuItem value={150}>2.5 hours</MenuItem>
                  <MenuItem value={180}>3 hours</MenuItem>
                </Select>
              </CompactFormControl>
            </Box>
          )}

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 0.75,
            mt: 2,
            pt: 2,
            borderTop: '1px solid #e0e0e0',
          }}>
            <ApplyButton onClick={handleApplyFilters}>
              Apply Filters
            </ApplyButton>
            
            <ClearButton onClick={handleClearFilters}>
              Clear All
            </ClearButton>
          </Box>
        </DrawerContent>
      </Drawer>
    </FilterContainer>
  );
};

export default FilterPanel;